import { Component, EventEmitter, Input, OnDestroy, Output, TemplateRef } from '@angular/core'
import { NavigationEnd, Router } from '@angular/router'
import { ITourStepFocus, TourFocusService } from '@employer/app/modules/assistant/services/tour-focus.service'
import { navTourAnchor } from '@employer/app/services/nav-tour-anchor'
import { INavigationItem } from '@engineering11/ui-lib/e11-nav-left'
import { Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'

/**
 * App-owned left-nav menu (expanded rows). One instance per surface — the desktop rail
 * and the mobile drawer each render their own, so each keeps independent expand state
 * and, critically, the menu renders exactly ONCE per surface. (The ui-lib `e11-nav-left`
 * drawer looped the menu once per bottom tab whose routeGroup matched — with four tabs
 * on 'main' that quadrupled every item. R7.)
 *
 * Route-driven expansion (R5): on load and after every navigation, any group whose
 * `isOpen(url)` ownership function claims the current URL is opened. Nothing is ever
 * auto-closed — only an explicit click on the group row collapses it (R1).
 *
 * Guided-tour exception (CNCT-6771): while a tour step is focusing a nav area, the tour owns expand
 * state — only that step's group is open, so walking from Openings to Engage visibly collapses Openings.
 * Route-driven expansion stands down for the duration (a step's own route would otherwise re-open the
 * group it just collapsed), and the user's own expand/collapse state is snapshotted on entry and put
 * back when the tour ends, so a walk never permanently clobbers it.
 */
@Component({
  selector: 'app-nav-menu',
  template: `
    <nav class="e11-flex e11-flex-col e11-text-skin-light" [attr.aria-label]="ariaLabel">
      <app-nav-favorites-section [items]="items" (leafNavigated)="leafNavigated.emit($event)"></app-nav-favorites-section>
      <app-nav-item
        *ngFor="let item of items"
        [item]="item"
        [level]="0"
        [openState]="openState"
        [customTemplates]="customTemplates"
        (leafNavigated)="leafNavigated.emit($event)"
      ></app-nav-item>
    </nav>
  `,
})
export class NavMenuComponent implements OnDestroy {
  @Input() ariaLabel = 'Primary'
  @Input() customTemplates: Record<string, TemplateRef<unknown>> = {}
  @Output() leafNavigated = new EventEmitter<INavigationItem>()

  openState = new Map<INavigationItem, boolean>()

  /** The user's expand state, parked while the tour drives the rail; `null` when no tour is driving it. */
  private preTourOpenState: Map<INavigationItem, boolean> | null = null
  /** `data-tour-id` of the group the current tour step wants open (`null` = collapse them all). */
  private tourNavFocus: string | null = null

  private _items: INavigationItem[] = []
  private destroy$ = new Subject<void>()

  @Input() set items(value: INavigationItem[] | null) {
    this._items = value ?? []
    if (this.tourFocus.engaged) {
      this.collapseToTourFocus()
      return
    }
    this.applyRouteOpenState(this.router.url)
  }
  get items(): INavigationItem[] {
    return this._items
  }

  constructor(private router: Router, private tourFocus: TourFocusService) {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(event => this.applyRouteOpenState(event.urlAfterRedirects))
    this.tourFocus.focus$.pipe(takeUntil(this.destroy$)).subscribe(focus => this.applyTourFocus(focus))
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  // Open (never close) every group whose ownership function claims the URL — R5. Stands down while a
  // tour step owns expand state, since the step's own route would re-open what the step just collapsed.
  private applyRouteOpenState(url: string, items: INavigationItem[] = this._items): void {
    if (this.tourFocus.engaged) return
    for (const item of items) {
      if (!item.children?.length) continue
      const owned = typeof item.isOpen === 'function' ? item.isOpen(url) : item.isOpen === true
      if (owned) this.openState.set(item, true)
      this.applyRouteOpenState(url, item.children)
    }
  }

  /** Hand the rail to the tour (snapshotting the user's state on the way in), or give it back on `null`. */
  private applyTourFocus(focus: ITourStepFocus | null): void {
    if (!focus) {
      if (this.preTourOpenState) {
        this.openState.clear()
        this.preTourOpenState.forEach((open, item) => this.openState.set(item, open))
        this.preTourOpenState = null
      }
      this.tourNavFocus = null
      return
    }
    if (!this.preTourOpenState) this.preTourOpenState = new Map(this.openState)
    this.tourNavFocus = focus.nav
    this.collapseToTourFocus()
  }

  /** Exactly one group open — the current step's — at every depth. */
  private collapseToTourFocus(items: INavigationItem[] = this._items): void {
    for (const item of items) {
      if (!item.children?.length) continue
      this.openState.set(item, navTourAnchor(item) === this.tourNavFocus)
      this.collapseToTourFocus(item.children)
    }
  }
}
